import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Upload,
  Button,
  message,
  UploadProps,
  Row,
  Col,
  Select,
  ColorPicker,
} from 'antd';
import {
  UploadOutlined,
  FilePdfOutlined,
  DeleteOutlined,
  PlusOutlined,
  FileWordOutlined,
  FileImageOutlined,
  FileUnknownOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { addJobApplication, getStatus, createStatus, getAllSources } from '../../utils/API';

import { UploadChangeParam, UploadFile } from 'antd/es/upload';

interface JobApplicationFormValues {
  _id: string;
  full_name: string;
  date: moment.Moment;
  email: string;
  phone_number: string;
  city: string;
  technology: string;
  total_experience: number;
  relevant_experience: number;
  current_ctc: number;
  expected_ctc: number;
  source: string;
  resume?: string;
  jobDesc: string;
  remark?: string;
  status: string;
  position: string
}

interface Status {
  _id: string;
  name: string;
  color: string;
}

interface AddJobapplicationProps {
  visible: boolean;
  onClose: () => void;
  onTaskCreated: () => void;
}

const AddJobApplicationModal: React.FC<AddJobapplicationProps> = ({
  visible,
  onClose,
  onTaskCreated,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const imageUrl = process.env.REACT_APP_Image_URL || '';
  const [fileList, setFileList] = useState<any[]>([]);
  const [resumes, setResumes] = useState<Array<string | File>>([]);
  const [source, setSource] = useState<string | undefined>();
  const [otherSource, setOtherSource] = useState('');
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState<string>('#1677FF');
  const [isAddingStatus, setIsAddingStatus] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [sources, setSources] = useState<Array<string>>([]);
  const [position, setPosition] = useState<string | any>();




  const technologyOptions: Record<string, string[]> = {
    "Frontend Developer": [
      // 🌐 Core
      "HTML5",
      "CSS3",
      "JavaScript (ES6+)",
      "TypeScript",

      // ⚛️ Frameworks & Libraries
      "React",
      "Next.js",
      "Angular",
      "Vue.js",
      "Nuxt.js",
      "Svelte",
      "SvelteKit",

      // 🎨 Styling & UI
      "Tailwind CSS",
      "Bootstrap",
      "Material UI",
      "Chakra UI",
      "Ant Design",

      // 📦 State Management
      "Redux",
      "Zustand",
      "Recoil",
      "Pinia",

      // ⚡ Build Tools
      "Vite",
      "Webpack",

      // 🔧 Testing
      "Jest",
      "React Testing Library",
      "Cypress",
      "Playwright",

      // 📱 Cross-Platform
      "React Native",
      "Flutter (Web Support)",

      // 📐 Static Site / Modern Meta Frameworks
      "Astro",
      "Gatsby",

      // 🛠 Other
      "GraphQL (Apollo)",
      "Storybook",
      "ESLint",
      "Prettier"
    ],
    "Backend Developer": [
      // ⚙️ Languages
      "JavaScript (Node.js)",
      "TypeScript",
      "Python",
      "Java",
      "C#",
      "PHP",
      "Go",
      "Ruby",
      "Rust",
      "Kotlin",
      "Scala",

      // 🖥 Frameworks & Runtimes
      "Node.js",
      "Express.js",
      "NestJS",
      "Django",
      "Flask",
      "FastAPI",
      "Spring Boot",
      "Micronaut",
      "Quarkus",
      "Laravel",
      "Symfony",
      "CodeIgniter",
      "Ruby on Rails",
      "Phoenix (Elixir)",
      "Fiber (Go)",
      "Gin (Go)",
      "Actix (Rust)",
      "Rocket (Rust)",

      // 🗄 Databases
      "MySQL",
      "PostgreSQL",
      "SQLite",
      "MongoDB",
      "Redis",
      "MariaDB",
      "Oracle DB",
      "MS SQL Server",
      "Cassandra",
      "CouchDB",
      "Firebase Realtime DB",
      "DynamoDB",

      // 📡 APIs & Communication
      "REST API",
      "GraphQL",
      "gRPC",
      "WebSockets",

      // ☁️ Cloud & Serverless
      "AWS Lambda",
      "Google Cloud Functions",
      "Azure Functions",
      "Serverless Framework",

      // 🐳 DevOps Tools
      "Docker",
      "Kubernetes",

      // 🧪 Testing
      "JUnit",
      "PyTest",
      "Mocha",
      "Jest",
      "Supertest",
      "Postman/Newman"
    ],
    "Fullstack Developer": [
      // 🌐 JavaScript/TypeScript Stacks
      "MERN (MongoDB, Express.js, React, Node.js)",
      "MEAN (MongoDB, Express.js, Angular, Node.js)",
      "MEVN (MongoDB, Express.js, Vue.js, Node.js)",
      "Next.js + Node.js",
      "NestJS + React",
      "RedwoodJS (Fullstack React/GraphQL framework)",
      "Blitz.js (Next.js based fullstack framework)",

      // 🐘 Python Based
      "Django + React",
      "Django + Angular",
      "Flask + React",
      "FastAPI + Vue",

      // ☕ Java Based
      "Spring Boot + React",
      "Spring Boot + Angular",

      // 🐘 PHP Based
      "LAMP (Linux, Apache, MySQL, PHP)",
      "Laravel + Vue.js",
      "Laravel + React",
      "Symfony + React",

      // 🚀 Other Popular Stacks
      "Ruby on Rails + React",
      "Ruby on Rails + Hotwire",
      "Elixir Phoenix + LiveView",
      "Go + React",
      "Go + Vue",
      "ASP.NET Core + React",
      "ASP.NET Core + Angular",

      // ☁️ Modern Fullstack/Meta Frameworks
      "Remix (React Fullstack Framework)",
      "Nuxt.js (Vue Fullstack Framework)",
      "SvelteKit (Svelte Fullstack Framework)",
      "Astro (with React/Vue/Svelte integrations)"
    ],
    "App Developer": [
              // Native Platforms
            "Android (Kotlin, Java)",
            "iOS (Swift, Objective-C)",
            // Mobile App Frameworks
            "React Native",
            "Flutter",
            "Ionic",
            "Xamarin",
        ]
    // "UI/UX Designer": ["Figma", "Adobe XD", "Sketch", "InVision"],
    // "Business Development Executive": ["Lead Generation", "Client Communication", "Salesforce"],
  };

  const fetchSource = async () => {
    try {
      const getSource = await getAllSources(searchText, page);
      if (getSource) {
        let filteredSource = getSource.sources.filter((item: any) =>
          item.category.includes('JobApplication')
        );
        setSources(filteredSource);
      }

    } catch (error) {
      console.error('Error fetching source:', error);
    }
  }
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await getStatus();

        setStatuses(response.data);
      } catch (error) {
        console.error('Error fetching statuses:', error);
        message.error('Failed to fetch statuses.');
      }
    };

    fetchStatuses();
    fetchSource()
  }, [visible]);

  const handleDeleteDocument = (doc: string | File, index: number) => {
    const newResumes = [...resumes];
    newResumes.splice(index, 1);
    setResumes(newResumes);

    const newFileList = [...fileList];
    newFileList.splice(index, 1);
    setFileList(newFileList);
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setResumes([]);
    setOtherSource('');
    setIsStatusDropdownOpen(false);
    onClose();
  };

  const onFinish = async (values: JobApplicationFormValues) => {
    setIsLoading(true);
    try {
      const formData = new FormData();


      const finalValues = {
        ...values,
      };


      Object.entries(finalValues).forEach(([key, value]) => {
        if (key === 'date' && value && typeof value === 'object' && 'format' in value) {
          formData.append(key, value.format('YYYY-MM-DD'));
        } else if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });
      // // If no relevant technology chosen, fall back to position
      // if (!finalValues.technology && finalValues.position) {
      //   formData.append("technology", finalValues.position);
      // } else if (finalValues.technology) {
      //   formData.append("technology", finalValues.technology);
      // }

      // // Append other fields except technology (already handled)
      // Object.entries(finalValues).forEach(([key, value]) => {
      //   if (key === "technology") return; // skip, already appended
      //   if (value !== undefined && value !== null) {
      //     formData.append(key, value as string);
      //   }
      // });

      resumes.forEach((file) => {
        if (typeof file !== 'string') {
          formData.append('resume', file);
        }
      });

      await addJobApplication(formData);
      message.success('Job application submitted successfully!');
      handleCancel();
      onTaskCreated();
    } catch (error) {
      console.error('Error submitting job application:', error);
      message.error('Failed to submit job application. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNewStatus = () => {
    setIsAddingStatus(true);
    setNewStatusColor('#1677FF');
    setIsStatusDropdownOpen(true);
  };

  const handleSaveNewStatus = async () => {
    if (!newStatusName.trim()) {
      message.error('Please enter a status name');
      return;
    }

    try {
      setIsLoading(true);
      await createStatus({ name: newStatusName, color: newStatusColor });
      const res = await getStatus();
      setStatuses(res.data);
      setNewStatusName('');
      setNewStatusColor('#1677FF');
      setIsAddingStatus(false);
      setIsStatusDropdownOpen(false);
      message.success('Status added successfully!');
    } catch (error) {
      console.error('Error adding new status:', error);
      message.error('Failed to add new status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelAddStatus = () => {
    setIsAddingStatus(false);
    setNewStatusName('');
    setNewStatusColor('#1677FF');
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();

    if (ext === "pdf") return <FilePdfOutlined className="text-2xl text-red-500 mb-1" />;
    if (ext === "doc" || ext === "docx") return <FileWordOutlined className="text-2xl text-blue-500 mb-1" />;
    if (ext === "jpg" || ext === "jpeg" || ext === "png")
      return <FileImageOutlined className="text-2xl text-green-500 mb-1" />;

    // default fallback
    return <FileUnknownOutlined className="text-2xl text-gray-500 mb-1" />;
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered={false}
      destroyOnClose
      className="custom-modal"
      bodyStyle={{
        overflowY: "auto",
        fontFamily: "inter, sans-serif",
      }}
      style={{
        bottom: 20,
        top: 20,
        // maxHeight: 'calc(100vh - 30px)',
        margin: '0 auto',
        position: 'relative',
      }}
      styles={{
        header: {
          padding: "12px",
          position: "sticky",
          top: 0,
          backgroundColor: "#f1f5ffff",
        },
        content: {
          padding: 0,
          overflow: "hidden",
        },
      }}
      title={
        <div
          style={{
            color: "#4b5563",
            fontFamily: "inter, sans-serif",
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          Create New Job Application
        </div>
      }
    >
      <Form onFinish={onFinish} form={form} layout="horizontal"
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
        colon={false} style={{ margin: 0 }}>
        <div className="elements">
          <Form.Item
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                Full Name <span className="text-danger">*</span>
              </span>
            }
            name="full_name"
            required={false}
            rules={[{ required: true, message: 'Please enter your full name' }]}
          >
            <Input placeholder="Fullname" className="Allinput" />
          </Form.Item>

          <Form.Item
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                Date <span className="text-danger">*</span>
              </span>
            }
            name="date"
            required={false}
            rules={[{ required: true, message: 'Please select date' }]}
          >
            <DatePicker className="Allinput" format="DD-MM-YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                Email <span className="text-danger">*</span>
              </span>
            }
            name="email"
            required={false}
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="Email" className="Allinput" />
          </Form.Item>

          <Form.Item
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                Contact <span className="text-danger">*</span>
              </span>
            }
            name="phone_number"
            required={false}
            rules={[
              { required: true, message: 'Please enter your phone number' },
              { pattern: /^[0-9+\- ]+$/, message: 'Please enter a valid phone number' },
            ]}
          >
            <Input placeholder="Contact" className="Allinput" />
          </Form.Item>

          <Form.Item
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                City <span className="text-danger">*</span>
              </span>
            }
            name="city"
            required={false}
            rules={[{ required: true, message: 'Please enter your city' }]}
          >
            <Input placeholder="City" className="Allinput" />
          </Form.Item>

          {/* Position */}
          <Form.Item
            name="position"
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                Position <span className="text-red-500">*</span>
              </span>
            }
            required={false}

            rules={[{ required: true, message: "Please select the position" }]}
          >
            <Select
              placeholder="Select Position"
              className="CustomSelect"
              value={position}
              onChange={(value) => {
                setPosition(value);
                form.setFieldsValue({ technology: undefined });
              }}
            >
              <Select.Option value="Frontend Developer">Frontend Developer</Select.Option>
              <Select.Option value="Backend Developer">Backend Developer</Select.Option>
              <Select.Option value="Fullstack Developer">Fullstack Developer</Select.Option>
              <Select.Option value="App Developer">App Developer</Select.Option>
              
              <Select.Option value="UI/UX Designer">
                UI/UX Designer
              </Select.Option>
              <Select.Option value="Business Development Executive">
                Business Development Executive
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                Relevant Technology
              </span>
            }
            name="technology"
          >
            <Select
              placeholder="Select Technology"
              className="CustomSelect"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {technologyOptions[position]?.map((tech: any) => (
                <Select.Option key={tech} value={tech}>
                  {tech}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                Source <span className="text-danger">*</span>
              </span>
            }

            name="source"
            required={false}
            rules={[{ required: true, message: 'Please select the source' }]}
          >
            <Select
              placeholder="Select Source"
              className="CustomSelect"
              value={source}
              onChange={(value) => setSource(value)}
            >
              {sources.map((source: any) => (
                <Select.Option value={source._id}>{source.name}</Select.Option>
              ))}
            </Select>

          </Form.Item>

          <Form.Item
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Total Experience<span className="text-danger">*</span> </span>}
            name="total_experience"
            required={false}
            rules={[{ required: true, message: 'Please enter total experience' }]}
          >
            <Input className="Allinput" placeholder="Total experience" />
          </Form.Item>

          <Form.Item
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Relevant Experience <span className="text-danger">*</span></span>}
            name="relevant_experience"
            required={false}
            rules={[{ required: true, message: 'Please enter relevant experience' }]}
          >
            <Input className="Allinput" placeholder="Relevant experience" />
          </Form.Item>

          <Form.Item
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Current CTC <span className="text-danger">*</span> </span>}
            name="current_ctc"
            required={false}
            rules={[{ required: true, message: 'Please enter current CTC' }]}
          >
            <Input className="Allinput" placeholder="Current CTC" />
          </Form.Item>

          <Form.Item
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Expected CTC <span className="text-danger">*</span></span>}
            name="expected_ctc"
            required={false}
            rules={[{ required: true, message: 'Please enter expected CTC' }]}
          >
            <Input className="Allinput" placeholder="Expected CTC" />
          </Form.Item>

          <Form.Item
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                Status <span className="text-danger">*</span>
              </span>
            }
            name="status"
            required={false}
            rules={[{ required: true, message: 'Please select the status' }]}
          >
            <Select
              placeholder="Status"
              className="CustomSelect"
              open={isStatusDropdownOpen}
              onDropdownVisibleChange={(open) => setIsStatusDropdownOpen(open)}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div style={{
                    position: 'sticky',
                    bottom: 0,
                    background: '#fff',
                    padding: '3px',
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {isAddingStatus ? (
                      <div className='flex flex-col py-2.5 px-2 '>
                        <div className="flex">
                          <Input
                            placeholder="Enter Status Name"
                            value={newStatusName}
                            onChange={(e) => setNewStatusName(e.target.value)}
                            style={{ marginBottom: '8px', width: '90%', height: '35px', borderColor: "#d9d9d9", borderRadius: "5px" }}
                          />
                          <ColorPicker
                            size='small'
                            value={newStatusColor}
                            onChange={(color) => setNewStatusColor(color.toHexString())}
                            onChangeComplete={(color) => setNewStatusColor(color.toHexString())}
                            getPopupContainer={(trigger: any) => trigger.parentElement}
                            style={{ margin: 'auto', padding: "5px" }}
                          />
                        </div>
                        <div className='flex w-full justify-end gap-2'>

                          <Button style={{ width: "140px", height: "35px", borderRadius: '4px' }} size="small" onClick={handleCancelAddStatus} >
                            Cancel
                          </Button>
                          <Button
                            style={{ width: "140px", height: "35px", backgroundColor: "#001529", color: "#ffffff" }}
                            size="small"
                            onClick={handleSaveNewStatus}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: '4px', width: '100%', display: 'flex', justifyContent: "end" }}>
                        <Button
                          icon={<PlusOutlined />}
                          onClick={handleAddNewStatus}
                          style={{ backgroundColor: "#001529", color: "#ffffff", border: 'none' }}

                        >
                          Add New Status
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            >
              {statuses.map((status) => (
                <Select.Option key={status._id} value={status._id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {status.name}
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* <Form.Item
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                Remark
              </span>
            }
            name="remark"
            required={false}
            rules={[{ required: false, message: 'Enter remark' }]}
          >
            <Input
              placeholder="Enter remark"
              className="Allinput"
            />
          </Form.Item> */}

          <Form.Item
            label={
              <span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>
                Description <span className="text-red-500">*</span>
              </span>
            }
            name="jobDesc"
            required={false}
            rules={[{ required: true, message: 'Enter job description' }]}
          >
            <Input.TextArea
              placeholder="Enter job description"
              className="Allinputtextarea"
            />
          </Form.Item>

          <Col xs={24} sm={24} md={24} xl={24} xxl={24}>
            <Row className="bg-white">


              <Col span={24}>
                <Form.Item
                  label={<span style={{
                    color: '#4b5563',
                    fontFamily: 'inter, sans-serif',
                    fontSize: '14px',
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}>Resume</span>}
                  name="resume"
                  valuePropName="fileList"
                  getValueFromEvent={(e) => e.fileList}
                >
                  <div className="flex items-center gap-4">
                    {/* Always show Upload button but disable when file exists */}
                    <Upload
                      action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
                      listType="picture-card"
                      maxCount={1}
                      showUploadList={false}
                      disabled={resumes.length > 0} // Disable when file exists
                      onChange={(info: UploadChangeParam<UploadFile<File>>) => {
                        if (info.fileList.length > 0 && resumes.length === 0) {
                          const newFile = info.fileList[0].originFileObj;
                          if (newFile) {
                            setResumes([newFile]);
                            setFileList([{
                              uid: `new-${Date.now()}`,
                              name: newFile.name,
                              status: 'done',
                              url: URL.createObjectURL(newFile),
                            }]);
                          }
                        }
                      }}
                      beforeUpload={() => false}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        disabled={resumes.length > 0} // Visual disabled state
                      >
                        Upload
                      </Button>
                    </Upload>

                    {/* Show uploaded file preview if exists */}
                    {resumes.map((doc, index) => {
                      if (!doc) return null;

                      const fileName = typeof doc === 'string'
                        ? doc.split('/').pop() || ''
                        : (doc as File).name;
                      const fileUrl = typeof doc === 'string'
                        ? `${imageUrl}${doc}`
                        : URL.createObjectURL(doc as File);

                      return (
                        <div
                          key={index}
                          className="w-38 h-32 border-2 border-gray-200 rounded-lg bg-white relative hover:shadow-md transition-all flex flex-col items-center justify-center p-4"
                        >
                          {getFileIcon(fileName)} {/* ✅ dynamic icon */}

                          <span className="text-xs font-medium text-gray-700 truncate w-full text-center">
                            {fileName}
                          </span>

                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute inset-0"
                            onClick={(e) => e.stopPropagation()}
                          ></a>

                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteDocument(doc, index);
                            }}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 cursor-pointer bg-white rounded-full p-1 flex items-center justify-center"
                            style={{ width: "22px", height: "22px" }}
                          >
                            <DeleteOutlined style={{ fontSize: "12px" }} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </div>
        <Row justify="end">
          <Col
            xs={24}
            sm={24}
            md={24}
            xl={24}
            xxl={20}
            className="d-flex justify-content-end me-4"
          >
            <div className="button-wrapper">
              <button
                className="allcanclebutton"
                onClick={onClose}
                type="button"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="allsubmitebutton w-20"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Adding...' : 'Add'}
              </button>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddJobApplicationModal;